import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Crown, Building2, Phone, Mail, MapPin, Save, Ticket as TicketIcon, Plus } from "lucide-react";
import { StatusBadge, PriorityBadge, TicketIdBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { CustomerForm } from "./Customers";

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const [c, t] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/tickets`),
      ]);
      setCustomer(c.data);
      setForm({
        name: c.data.name || "", email: c.data.email || "", phone: c.data.phone || "",
        organization: c.data.organization || "", department: c.data.department || "",
        location: c.data.location || "", notes: c.data.notes || "", vip: !!c.data.vip,
      });
      setTickets(t.data || []);
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), phone: form.phone || null, organization: form.organization || null, department: form.department || null, location: form.location || null, notes: form.notes || null, vip: !!form.vip };
      const { data } = await api.patch(`/customers/${id}`, payload);
      setCustomer(data);
      setDirty(false);
      toast.success("Customer updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const startTicket = () => {
    // Deep-link to /tickets/new with the customer prefilled
    nav(`/tickets/new?reporter_id=${customer.user_id}`);
  };

  if (!customer || !form) return <div className="text-slate-500 font-mono text-xs uppercase tracking-[0.3em]">Loading…</div>;

  const open = tickets.filter((t) => !["resolved", "closed", "cancelled"].includes(t.status));
  const done = tickets.filter((t) => ["resolved", "closed", "cancelled"].includes(t.status));

  return (
    <div className="space-y-8">
      <Link to="/customers" data-testid="back-customers" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> All customers
      </Link>

      <div className="flex items-center gap-5 flex-wrap">
        <Avatar className="h-16 w-16 rounded-sm">
          <AvatarFallback className="rounded-sm bg-slate-200 text-lg">{customer.name?.slice(0,2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="customer-name">{customer.name}</h1>
            {customer.vip && <span data-testid="customer-vip-badge" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 border rounded-sm bg-amber-500/15 text-amber-300 border-amber-500/40"><Crown className="h-3 w-3" strokeWidth={2} /> VIP</span>}
          </div>
          <div className="mt-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 font-mono">
            <span className="flex items-center gap-1"><Mail className="h-3 w-3" strokeWidth={1.5} />{customer.email}</span>
            {customer.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" strokeWidth={1.5} />{customer.phone}</span>}
            {customer.organization && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" strokeWidth={1.5} />{customer.organization}</span>}
            {customer.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" strokeWidth={1.5} />{customer.location}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={startTicket} data-testid="customer-new-ticket-btn" className="bg-red-600 hover:bg-red-500 rounded-sm h-11 px-5">
            <Plus className="h-4 w-4" strokeWidth={2} /> New ticket
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile edit */}
        <div className="lg:col-span-1 border border-slate-200 bg-white p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Profile</div>
          <div className="mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Email</div>
            <Input value={form.email} disabled className="bg-white border-slate-200 rounded-sm h-10 opacity-60" data-testid="profile-email" />
            <div className="text-[10px] text-slate-600 mt-1">Email is locked — it's the customer's ID.</div>
          </div>
          <CustomerForm form={form} set={set} />
          <label className="mt-4 flex items-center gap-2 text-xs text-amber-300 cursor-pointer" data-testid="profile-vip-toggle">
            <input type="checkbox" checked={form.vip} onChange={(e) => set("vip", e.target.checked)} className="accent-amber-400" />
            <Crown className="h-3.5 w-3.5" strokeWidth={1.8} /> Mark as VIP · gold badge on all their tickets
          </label>
          <Button onClick={save} disabled={!dirty || saving} data-testid="profile-save-btn" className="mt-5 w-full bg-red-600 hover:bg-red-500 disabled:bg-slate-700 rounded-sm">
            <Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>

        {/* Tickets */}
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-3 border border-slate-200">
            <Stat label="Total" value={tickets.length} testid="stat-total" />
            <Stat label="Open" value={open.length} accent={open.length > 0 ? "text-red-600" : ""} testid="stat-open" />
            <Stat label="Resolved" value={done.length} accent={done.length > 0 ? "text-emerald-400" : ""} testid="stat-done" />
          </div>
          <div className="border border-slate-200 bg-white" data-testid="customer-tickets">
            <div className="p-4 border-b border-slate-200 flex items-center gap-2">
              <TicketIcon className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Ticket history</div>
            </div>
            {tickets.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">No tickets yet for this customer.</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {tickets.map((t) => (
                  <Link key={t.ticket_id} to={`/tickets/${t.ticket_id}`} data-testid={`hist-${t.ticket_number}`} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-100">
                    <TicketIdBadge id={t.ticket_number} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{t.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{t.category_name || "Uncategorized"} · {t.assignee_name || "Unassigned"}</div>
                    </div>
                    <PriorityBadge priority={t.priority} />
                    <StatusBadge status={t.status} />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="p-5 border-r last:border-r-0 border-slate-200 bg-white" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{label}</div>
      <div className={`mt-2 font-display font-black text-3xl ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  );
}
